import React, { useState } from 'react';
import { Plus, Calendar, Trophy, Loader2, AlertCircle, Search } from 'lucide-react';
import { countries } from '../ui/CountrySelector';
import { API_BASE_URL } from '../../config/api';

// Dance types
const DANCE_TYPES = {
  all: "All Dance Types",
  // Ballroom Dances
  "INTERNATIONAL WALTZ": "International Waltz",
  "INTERNATIONAL TANGO": "International Tango",
  "INTERNATIONAL VIENNESE WALTZ": "International Viennese Waltz",
  "INTERNATIONAL SLOW FOX TROT": "International Slow Fox Trot",
  "INTERNATIONAL QUICKSTEP": "International Quickstep",
  "AMERICAN SMOOTH WALTZ": "American Smooth Waltz",
  "AMERICAN SMOOTH TANGO": "American Smooth Tango",
  "AMERICAN SMOOTH VIENNESE WALTZ": "American Smooth Viennese Waltz",
  "AMERICAN SMOOTH FOX TROT": "American Smooth Fox Trot",
  
  // Latin Dances
  "INTERNATIONAL SAMBA": "International Samba",
  "INTERNATIONAL CHA CHA CHA": "International Cha Cha Cha",
  "INTERNATIONAL RUMBA": "International Rumba",
  "INTERNATIONAL PASO DOBLE": "International Paso Doble",
  "INTERNATIONAL JIVE": "International Jive",
  
  // Duo Dances
  "CONTEMPORARY": "Contemporary",
  "KRUMPING": "Krumping",
  "CROSSDANCE": "Crossdance",
  "TRADITIONAL": "Traditional",
  "HIP HOP": "Hip Hop",
  "BACHATA": "Bachata",
  "SALSA": "Salsa",
  "FREESTYLE": "Freestyle",
  "MAMBO": "Mambo",
  "AFROBEAT": "Afrobeat",
  "BALLET": "Ballet",
  
  // Formation Dances
  "FORMATION STANDARD": "Formation Standard",
  "FORMATION LATIN": "Formation Latin",
  "FORMATION MIXED": "Formation Mixed",
  "FORMATION THEATRICAL": "Formation Theatrical",
  "FORMATION TRADITIONAL": "Formation Traditional"
};

const EventManagement = ({ getIdToken }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    dance_type: 'all',
    max_entries: 0,
    country_restriction: false,
    allowed_countries: [],
    analysis_type: 'couple' // Add default analysis_type
  });

  const handleCountryToggle = (country) => {
    const currentCountries = [...formData.allowed_countries];
    const index = currentCountries.indexOf(country);
    
    if (index > -1) {
      currentCountries.splice(index, 1);
    } else {
      currentCountries.push(country);
    }
    
    setFormData({ ...formData, allowed_countries: currentCountries });
  };

  const handleSelectAllCountries = () => {
    setFormData({ ...formData, allowed_countries: [...countries] });
  };

  const handleClearCountries = () => {
    setFormData({ ...formData, allowed_countries: [] });
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      
      // If no countries selected or country restriction is off, set to 'all'
      const allowed_countries = !formData.country_restriction || formData.allowed_countries.length === 0 
        ? ['all'] 
        : formData.allowed_countries;
      
      const response = await fetch(`${API_BASE_URL}/admin/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          allowed_countries,
          entry_fee: 2,
          status: 'active',
          analysis_type: formData.analysis_type || 'couple'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create event');
      }
      
      const result = await response.json();
      setShowCreateForm(false);
      // Reset form
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        dance_type: 'all',
        max_entries: 0,
        country_restriction: false,
        allowed_countries: []
      });
      
      // Refresh events list
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800 flex items-center">
          <Trophy className="w-5 h-5 mr-2" />
          Event Management
        </h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New Event</h3>
            <form onSubmit={handleCreateEvent}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Event Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                    rows="3"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="datetime-local"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="datetime-local"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Analysis Type</label>
                  <select
                    value={formData.analysis_type}
                    onChange={(e) => setFormData({...formData, analysis_type: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="couple">Couple Analysis</option>
                    <option value="solo">Solo Analysis</option>
                    <option value="duo">Duo Analysis</option>
                    <option value="formation">Formation Analysis</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Dance Type</label>
                  <select
                    value={formData.dance_type}
                    onChange={(e) => setFormData({...formData, dance_type: e.target.value})}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                  >
                    {Object.entries(DANCE_TYPES).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="country_restriction"
                      checked={formData.country_restriction}
                      onChange={(e) => setFormData({...formData, country_restriction: e.target.checked})}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="country_restriction" className="ml-2 block text-sm text-gray-900">
                      Restrict to specific countries
                    </label>
                  </div>
                </div>

                {formData.country_restriction && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allowed Countries
                      <div className="mt-1 space-x-2">
                        <button
                          type="button"
                          onClick={handleSelectAllCountries}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={handleClearCountries}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Clear All
                        </button>
                      </div>
                    </label>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto border rounded p-2">
                      {countries.map((country) => (
                        <label key={country} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.allowed_countries.includes(country)}
                            onChange={() => handleCountryToggle(country)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{country}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </span>
                  ) : (
                    'Create Event'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;